import type { CSSProperties, ReactNode } from "react";

const border = "#2a2a3a";
const muted = "#9292aa";
const blue = "#7180ff";

type WorkspaceSection = "overview" | "audit" | "seo" | "autopilot" | "drafts" | "trends" | "work" | "results" | "cms" | "settings";

interface Props {
  clientId?: string | null;
  active?: WorkspaceSection;
  children?: ReactNode;
}

const topLink: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 7,
  border: `1px solid ${border}`,
  borderRadius: 9,
  padding: "8px 11px",
  textDecoration: "none",
  color: "#d7d7e3",
  fontSize: 11,
  fontWeight: 750,
  background: "#14141b",
};

function workspaceHref(section: WorkspaceSection, clientId: string): string {
  const id = encodeURIComponent(clientId);
  if (section === "overview") return `/clients/${id}`;
  if (section === "audit") return `/website-review?clientId=${id}`;
  if (section === "seo") return `/seo?clientId=${id}&tab=opportunities`;
  if (section === "autopilot") return `/clients/${id}/autopilot`;
  if (section === "drafts") return `/clients/${id}/expand-drafts`;
  if (section === "trends") return `/clients/${id}/page-trends`;
  if (section === "work") return `/clients/${id}#work`;
  if (section === "results") return `/clients/${id}#results`;
  if (section === "cms") return `/?clientId=${id}&tab=pseo_clients`;
  return `/?clientId=${id}&tab=dashboard`;
}

const sections: Array<{ key: WorkspaceSection; label: string; phase: "live" | "bridge" }> = [
  { key: "overview", label: "Overview", phase: "live" },
  { key: "audit", label: "Audit & QA", phase: "live" },
  { key: "seo", label: "SEO", phase: "live" },
  { key: "autopilot", label: "SEO Autopilot", phase: "live" },
  { key: "drafts", label: "EXPAND Drafts", phase: "live" },
  { key: "trends", label: "Page Movement", phase: "live" },
  { key: "work", label: "Actions / Work", phase: "live" },
  { key: "results", label: "History & Results", phase: "live" },
  { key: "cms", label: "CMS / pSEO", phase: "bridge" },
  { key: "settings", label: "Integrations / Settings", phase: "bridge" },
];

export default function ClientOperatorNav({ clientId, active = "overview", children }: Props) {
  return <>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
      <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
        <a href="/clients" style={{ ...topLink, borderColor: clientId ? border : blue, color: clientId ? "#d7d7e3" : "#eef0ff" }}>▦ Clients</a>
        <a href="/seo-work-queue" style={topLink}>☑ Today&apos;s Work</a>
        <a href={"/content"+(clientId?"?clientId="+encodeURIComponent(clientId):"")} style={{...topLink,borderColor:"#6574ee",color:"#dce0ff"}}>✎ New Content</a>
        <a href={"/seo-launch"+(clientId?"?clientId="+encodeURIComponent(clientId):"")} style={{...topLink,borderColor:"#6574ee",color:"#dce0ff"}}>▶ SEO Launch Mode</a>
        <a href="/pseo-generation" style={{...topLink,borderColor:"#6574ee",color:"#dce0ff"}}>▶ pSEO Generation</a>
        <a href="/seo-pilots" style={topLink}>◎ Owned-site pilots</a>
        <a href="/sl-plumbing-pilot" style={topLink}>↗ SL Plumbing audit</a>
      </div>
      {children}
    </div>

    {clientId && <nav aria-label="Client workspace" style={{ display: "flex", gap: 5, overflowX: "auto", padding: "5px", background: "#101016", border: `1px solid ${border}`, borderRadius: 11, marginBottom: 18 }}>
      {sections.map(section => {
        const selected = section.key === active;
        return <a
          key={section.key}
          href={workspaceHref(section.key, clientId)}
          title={section.phase === "bridge" ? "Uses the existing proven Content Tool surface while the client shell is phased in." : undefined}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            whiteSpace: "nowrap",
            textDecoration: "none",
            borderRadius: 8,
            padding: "8px 10px",
            background: selected ? "rgba(113,128,255,.16)" : "transparent",
            color: selected ? "#dce0ff" : muted,
            border: `1px solid ${selected ? "rgba(113,128,255,.38)" : "transparent"}`,
            fontSize: 10,
            fontWeight: selected ? 850 : 700,
          }}
        >
          {section.label}
          {section.phase === "bridge" && <span style={{ color: "#64647c", fontSize: 8 }}>↗</span>}
        </a>;
      })}
    </nav>}
  </>;
}
